/**
 * The "More" surface — everything Wallet does not have but AIM does.
 *
 * Wallet's five-slot bar covers a personal expense tracker. AIM is also a rent
 * business, a tax workbench, a loan tracker, an investment ledger and a
 * document vault for eight people. Those do not belong in the daily flow and
 * they certainly do not belong as a seventh bottom tab, so they live one tap
 * away, grouped, with a line of context each.
 *
 * EVERY TILE IS A PAGE THAT ALREADY EXISTS. `moreSections()` builds the list
 * from `PAGE_REGISTRY` filtered by the user's own visible set, plus the three
 * non-registry routes the parity test already accounts for and the admin
 * console (role-gated by `isAdminOnlyPath`, exactly as it is today). Adding a
 * page to the registry makes it appear here automatically; nothing here can
 * make a page appear that the registry does not know.
 */
import { useMemo } from "react";
import Link from "@/compat/link";
import { useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Lift } from "@/lib/motion";
import type { PageKey } from "@/lib/rbac/pages";
import { Sheet } from "./Sheet";
import { moreSections } from "./nav";
import { availableScreens } from "./screens";

export interface MoreSheetProps {
  open: boolean;
  onClose(): void;
  visible: ReadonlySet<PageKey>;
  isAdmin: boolean;
}

// W13 audit straggler (a): `nav.ts`'s `ADMIN_ENTRIES` still lists
// `/admin/design-system` — a dev-only design sandbox `rent-extras.routes.tsx`
// documents as DELIBERATELY not routed (no page in `web/` renders it; the
// Next.js source it once lived in is gone from the repo entirely). Following
// it from here 404s. `nav.ts`/`TopBar.tsx` (the desktop admin menu's own copy
// of this same dead link) are outside this file's edit scope for this pass,
// so the entry is filtered out at render time here rather than deleted at the
// source — same user-facing fix, narrower blast radius.
const UNROUTED_HREFS = new Set(["/admin/design-system"]);

export function MoreSheet({ open, onClose, visible, isAdmin }: MoreSheetProps) {
  const { pathname } = useLocation();
  const sections = useMemo(() => {
    return moreSections(visible, isAdmin)
      .map((section) => ({
        ...section,
        entries: section.entries.filter((entry) => !UNROUTED_HREFS.has(entry.href)),
      }))
      .filter((section) => section.entries.length > 0);
  }, [visible, isAdmin]);

  return (
    <Sheet open={open} onClose={onClose} title="More">
      <div className="px-3 pb-4 pt-1">
        {sections.map((section) => (
          <section key={section.title} className="mb-1">
            <p className="cp-micro-label px-2 pb-1.5 pt-3">{section.title}</p>
            <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
              {section.entries.map((entry) => {
                const active =
                  pathname === entry.href || pathname.startsWith(entry.href + "/");
                return (
                  <Lift key={entry.href} className="w-full" rounded="rounded-[var(--cp-r-card)]">
                    <Link
                      href={entry.href}
                      onClick={onClose}
                      aria-current={active ? "page" : undefined}
                      className={cn(
                        "flex min-h-[76px] flex-col gap-1.5 rounded-[var(--cp-r-card)] border p-3 transition-colors",
                        active
                          ? "border-[var(--cp-accent)] bg-[var(--cp-accent-wash)]"
                          : "border-[var(--cp-line-2)] bg-[var(--cp-surface-2)] hover:border-[var(--cp-line)]",
                      )}
                    >
                      <entry.icon
                        className={cn(
                          "h-[18px] w-[18px] shrink-0",
                          active ? "text-[var(--cp-accent-ink)]" : "text-[var(--cp-ink-3)]",
                        )}
                      />
                      <span
                        className={cn(
                          "text-[13px] font-semibold leading-tight",
                          active ? "text-[var(--cp-accent-ink)]" : "text-[var(--cp-ink)]",
                        )}
                      >
                        {entry.label}
                      </span>
                      {entry.hint && (
                        <span className="line-clamp-2 text-[11px] leading-snug text-[var(--cp-ink-3)]">
                          {entry.hint}
                        </span>
                      )}
                    </Link>
                  </Lift>
                );
              })}
            </div>
          </section>
        ))}

        {/*
          Dev-only. While the four screen packages are being built in parallel,
          "is the Home screen in this build yet?" is the question everyone asks,
          and answering it in the UI beats reading a glob.
        */}
        {import.meta.env.DEV && (
          <p className="px-2 pt-4 text-[11px] text-[var(--cp-ink-3)]">
            Wallet screens in this build: {availableScreens().join(", ") || "none yet"} — the rest
            fall back to the classic pages.
          </p>
        )}
      </div>
    </Sheet>
  );
}
